import React from 'react';
import { CopyIcon } from './CopyIcon';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

interface CopyButtonProps {
    textToCopy: string;
    variant?: 'dark' | 'light';
    size?: number;
    className?: string;
    onCopy?: (success: boolean) => void;
    showTooltip?: boolean;
    tooltipText?: string;
    successTooltipText?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
    textToCopy,
    variant = 'dark',
    size = 24,
    className = '',
    onCopy,
    showTooltip = true,
    tooltipText = 'Copy to clipboard',
    successTooltipText = 'Copied!'
}) => {
    const { copied, copyToClipboard } = useCopyToClipboard(2000);

    const handleCopy = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const success = await copyToClipboard(textToCopy);
        onCopy?.(success);
    };

    const buttonClassName = `
        inline-flex items-center justify-center
        cursor-pointer
        transition-all duration-200
        hover:opacity-70
        active:scale-95
        relative
        group
        ${className}
    `.trim();

    return (
        <button
            onClick={handleCopy}
            className={buttonClassName}
            title={copied ? successTooltipText : tooltipText}
            aria-label={copied ? successTooltipText : tooltipText}
        >
            <CopyIcon
                variant={variant}
                size={size}
                className={`transition-all duration-200 ${copied ? 'opacity-50' : ''}`}
            />

            {/* Tooltip opcional */}
            {showTooltip && (
                <div className="
                    absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
                    px-2 py-1 text-xs text-white bg-gray-900 rounded
                    opacity-0 group-hover:opacity-100
                    transition-opacity duration-200
                    pointer-events-none
                    whitespace-nowrap
                    z-10
                ">
                    {copied ? successTooltipText : tooltipText}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-900"></div>
                </div>
            )}
        </button>
    );
}; 